import type { ReactNode } from 'react';
import type { BombiixCellRendererParams, BombiixRowId } from '../types';

interface RowActionsExtras {
  onAddRow?: () => void;
  onDeleteRow?: (rowId: BombiixRowId | undefined) => void;
  getRowId?: (row: unknown) => BombiixRowId | undefined;
  canAdd?: boolean;
  canDelete?: boolean;
  extras?: ReactNode;
}

type RowActionsCellProps = BombiixCellRendererParams & RowActionsExtras;

/**
 * Trailing actions cell. Renders a "+" on the last data row (when onAddRow is
 * supplied and canAdd !== false) and a trash icon on every row (when
 * onDeleteRow is supplied and canDelete !== false). Hidden on pinned footer
 * rows; a __cagTotal row is treated as a terminator like the pinned footer.
 */
function RowActionsCell(params: RowActionsCellProps) {
  const { data, node, api, onAddRow, onDeleteRow, getRowId, canAdd = true, canDelete = true, extras = null } = params;

  if (node?.rowPinned) return null;

  const rowId = getRowId ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;

  const isLastRow = (() => {
    if (!api || node?.rowIndex == null) return false;
    const next = api.getDisplayedRowAtIndex(node.rowIndex + 1);
    if (!next) return true;
    const nextData = next.data as { __cagTotal?: boolean } | undefined;
    return Boolean(next.rowPinned) || Boolean(nextData?.__cagTotal);
  })();

  return (
    <div className="cag-row-actions">
      {extras ? <span className="cag-row-actions__extras">{extras}</span> : null}
      {onAddRow && isLastRow && canAdd ? (
        <button type="button" className="bombiix-icon-container bombiix-icon-container--blue" title="Add row" aria-label="Add row" onClick={() => onAddRow()}>
          <i className="bombiix-icon-add" />
        </button>
      ) : null}
      {onDeleteRow && canDelete ? (
        <button
          type="button"
          className="bombiix-icon-container bombiix-icon-container--red"
          title="Delete row"
          aria-label="Delete row"
          onClick={() => onDeleteRow(rowId)}
        >
          <i className="bombiix-icon-delete" />
        </button>
      ) : null}
    </div>
  );
}

export default RowActionsCell;
